import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Plus, Search, Eye, Edit, Trash2, Upload, Download, BarChart3, ChevronDown, ChevronUp, Mail, Phone, Briefcase, Calendar, MapPin, Filter } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { formatDate, cn } from '../lib/utils';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSearchHighlight, HighlightedText } from '../hooks/useSearchHighlight';
import { useSmartRefresh, WS_EVENTS } from '../hooks/useSmartRefresh';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

// Grade configuration
const GRADE_CONFIG = {
  livreur: { label: 'Livreur', color: 'bg-blue-500' },
  responsable: { label: 'Responsable', color: 'bg-green-500' },
  manager: { label: 'Manager', color: 'bg-purple-500' },
  chef_entreprise: { label: 'Chef d\'entreprise', color: 'bg-red-500' }
};

// Function to determine grade from poste
const getGradeFromPoste = (poste) => {
  if (!poste) return 'livreur';
  const posteLower = poste.toLowerCase();
  if (posteLower.includes('chef') || posteLower.includes('directeur') || posteLower.includes('pdg')) return 'chef_entreprise';
  if (posteLower.includes('manager') || posteLower.includes('gestionnaire')) return 'manager';
  if (posteLower.includes('responsable')) return 'responsable';
  return 'livreur';
};

// Employee Card Component with floating dropdown
const EmployeeCard = ({ employee, highlightTerm, onDelete, onEdit, onView, onGradeChange, canEditGrade, hasAdminRole, deletingId, theme, isExpanded, onToggle }) => {
  const grade = getGradeFromPoste(employee.poste);
  const gradeConfig = GRADE_CONFIG[grade] || GRADE_CONFIG.livreur;

  return (
    <div className="relative" data-testid={`employee-card-${employee.id}`}>
      {/* Carte principale */}
      <button
        onClick={onToggle}
        className={cn(
          "w-full rounded-xl border transition-all flex items-center justify-between p-4 text-left",
          theme === 'dark' ? 'bg-zinc-900/50 border-white/10 hover:border-white/20' : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm',
          isExpanded && 'ring-2 ring-red-500/50'
        )}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span className={cn("font-semibold truncate", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
            <HighlightedText text={`${employee.prenom} ${employee.nom}`} searchTerm={highlightTerm} />
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span 
            className={cn("w-3 h-3 rounded-full", gradeConfig.color)}
            title={gradeConfig.label}
          />
          {isExpanded ? (
            <ChevronUp size={18} className={theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'} />
          ) : (
            <ChevronDown size={18} className={theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'} />
          )}
        </div>
      </button>

      {/* Menu déroulant flottant (survole les autres cartes) */}
      {isExpanded && (
        <div 
          className={cn(
            "absolute left-0 right-0 top-full mt-1 z-50 rounded-xl border shadow-2xl",
            theme === 'dark' ? 'bg-zinc-900 border-white/10' : 'bg-white border-gray-200'
          )}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="p-4">
            {/* Grade badge */}
            <div className="flex justify-center mb-3">
              <span className={cn(
                "text-xs px-4 py-1.5 rounded-full text-white font-bold",
                gradeConfig.color
              )}>
                {gradeConfig.label}
              </span>
            </div>
            
            {/* Informations en grille */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <Mail size={14} className={theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'} />
                <span className={cn("text-sm truncate", theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>
                  <HighlightedText text={employee.email} searchTerm={highlightTerm} />
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Phone size={14} className={theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'} />
                <span className={cn("text-sm", theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>
                  {employee.telephone || '-'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Briefcase size={14} className={theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'} />
                <span className={cn("text-sm truncate", theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>
                  <HighlightedText text={employee.poste} searchTerm={highlightTerm} />
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar size={14} className={theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'} />
                <span className={cn("text-sm", theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>
                  {formatDate(employee.date_embauche)}
                </span>
              </div>
              <div className="flex items-center gap-2 col-span-2">
                <MapPin size={14} className={theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'} />
                <span className={cn("text-sm", theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>
                  Station: <strong>{employee.station || 'Non définie'}</strong>
                </span>
              </div>
            </div>

            {/* Infos supplémentaires */}
            <div className={cn(
              "flex items-center gap-4 mt-3 pt-3 border-t text-sm",
              theme === 'dark' ? 'border-white/5 text-zinc-400' : 'border-gray-100 text-gray-500'
            )}>
              <span>Contrat: <strong className={theme === 'dark' ? 'text-zinc-200' : 'text-gray-700'}>{employee.type_contrat}</strong></span>
              {employee.matricule && (
                <span>Mat: <strong className={theme === 'dark' ? 'text-zinc-200' : 'text-gray-700'}>{employee.matricule}</strong></span>
              )}
            </div>

            {/* Modification du grade */}
            {canEditGrade && (
              <div className={cn(
                "mt-3 pt-3 border-t",
                theme === 'dark' ? 'border-white/5' : 'border-gray-100'
              )}>
                <p className={cn("text-xs font-medium mb-2", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
                  Modifier le grade:
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(GRADE_CONFIG).map(([key, config]) => {
                    const isActive = grade === key;
                    return (
                      <button
                        key={key}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isActive) onGradeChange(employee.id, key);
                        }}
                        className={cn(
                          "px-3 py-1.5 rounded-full text-xs font-bold transition-all text-center",
                          isActive
                            ? `${config.color} text-white`
                            : theme === 'dark'
                              ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        )}
                      >
                        {config.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className={cn(
              "flex gap-2 mt-3 pt-3 border-t",
              theme === 'dark' ? 'border-white/5' : 'border-gray-100'
            )}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onView(employee.id)}
                className={cn(
                  "flex-1",
                  theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white hover:bg-white/10' : 'border-gray-200 hover:bg-gray-100'
                )}
                data-testid={`view-employee-${employee.id}`}
              >
                <Eye size={16} className="mr-1" />
                Voir détails
              </Button>
              {hasAdminRole && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(employee.id)}
                    className="bg-blue-500/10 border-blue-500/30 text-blue-500 hover:bg-blue-500/20"
                    data-testid={`edit-employee-${employee.id}`}
                  >
                    <Edit size={16} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-red-500/10 border-red-500/30 text-red-500 hover:bg-red-500/20"
                    data-testid={`delete-employee-${employee.id}`}
                    disabled={deletingId === employee.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      console.log('Delete button clicked for:', employee.prenom, employee.nom);
                      const confirmed = window.confirm(`Êtes-vous sûr de vouloir supprimer ${employee.prenom} ${employee.nom} ? Cette action est irréversible.`);
                      console.log('Confirmation result:', confirmed);
                      if (confirmed) {
                        console.log('Calling onDelete for:', employee.id);
                        onDelete(employee.id);
                      }
                    }}
                  >
                    {deletingId === employee.id ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-400"></div>
                    ) : (
                      <Trash2 size={16} />
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [stationFilter, setStationFilter] = useState('all');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const { hasRole, user } = useAuth();
  const { theme } = useTheme();
  const { searchTerm: highlightTerm } = useSearchHighlight();

  // Liste des stations disponibles
  const STATIONS = [
    { value: 'CHRONO TLS', label: 'CHRONO TLS' },
    { value: 'CHRONO LIG', label: 'CHRONO LIG' },
    { value: 'CHRONO PAU', label: 'CHRONO PAU' },
    { value: 'FEDEX', label: 'FEDEX' },
    { value: 'AMAZON DAC2', label: 'AMAZON DAC2' },
    { value: 'AUTRES', label: 'AUTRES' }
  ];

  const canEditGrade = hasRole(['admin', 'rh', 'responsable', 'manager']);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on a dialog overlay or portal content
      if (event.target.closest('[role="alertdialog"]') || 
          event.target.closest('[data-radix-portal]') ||
          event.target.closest('.fixed.inset-0')) {
        return;
      }
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setExpandedId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    let filtered = employees;
    
    // Filtre par recherche
    if (searchTerm) {
      filtered = filtered.filter(emp =>
        `${emp.nom} ${emp.prenom} ${emp.email} ${emp.poste}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filtre par station
    if (stationFilter !== 'all') {
      filtered = filtered.filter(emp => emp.station === stationFilter);
    }
    
    setFilteredEmployees(filtered);
  }, [searchTerm, stationFilter, employees]);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data);
      setFilteredEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Smart refresh: WebSocket + polling fallback 30s (désactivé quand carte déployée)
  useSmartRefresh(fetchEmployees, {
    wsEvents: [WS_EVENTS.EMPLOYEE_UPDATED, WS_EVENTS.EMPLOYEE_CREATED, WS_EVENTS.EMPLOYEE_DELETED],
    fallbackInterval: 30000,
    enabled: !expandedId
  });

  const handleDeleteEmployee = async (employeeId) => {
    setDeletingId(employeeId);
    try {
      await api.delete(`/employees/${employeeId}`);
      toast.success('Salarié supprimé avec succès');
      setExpandedId(null);
      fetchEmployees();
    } catch (error) {
      console.error('Error deleting employee:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  const handleGradeChange = async (employeeId, newGrade) => {
    const gradeToPoste = {
      livreur: 'Livreur',
      responsable: 'Responsable',
      manager: 'Manager',
      chef_entreprise: 'Chef d\'entreprise'
    };

    try {
      await api.put(`/employees/${employeeId}`, { poste: gradeToPoste[newGrade] });
      toast.success('Grade mis à jour');
      fetchEmployees();
    } catch (error) {
      console.error('Error updating grade:', error);
      toast.error('Erreur lors de la mise à jour du grade');
    }
  };

  const handleToggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/employees/import/csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResult(response.data);
      if (response.data.importes > 0) {
        toast.success(`${response.data.importes} salarié(s) importé(s) !`);
        fetchEmployees();
      }
      if (response.data.erreurs > 0) {
        toast.warning(`${response.data.erreurs} erreur(s) lors de l'import`);
      }
    } catch (error) {
      console.error('Error importing CSV:', error);
      toast.error('Erreur lors de l\'import');
      setImportResult({ success: false, erreur: error.response?.data?.detail || 'Erreur inconnue' });
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const downloadTemplate = () => {
    const template = `Matricule;Nom;Prenom;Email;Telephone;Poste;Type_Contrat;Date_Embauche;Jours_Max_Semaine
MAT9999;Dupont;Jean;jean.dupont@exemple.fr;0612345678;Chauffeur;CDI;2023-01-15;5`;
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'modele_import_salaries.csv';
    link.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-zinc-800 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-red-500 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 px-1 md:p-0 pb-24 md:pb-0" data-testid="employees-page" ref={containerRef}>
      {/* Header - All on one line on mobile */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-zinc-500" size={16} />
          <Input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={cn(
              "pl-8 h-9 text-sm",
              theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white placeholder:text-zinc-500' : 'bg-white border-gray-200'
            )}
            data-testid="search-input"
          />
        </div>
        
        {/* Filtre par station */}
        <Select value={stationFilter} onValueChange={setStationFilter}>
          <SelectTrigger className={cn(
            "w-[140px] h-9 text-sm shrink-0",
            theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white' : 'bg-white border-gray-200'
          )}>
            <Filter size={14} className="mr-1" />
            <SelectValue placeholder="Station" />
          </SelectTrigger>
          <SelectContent className={theme === 'dark' ? 'bg-zinc-800 border-white/10' : ''}>
            <SelectItem value="all">Toutes</SelectItem>
            {STATIONS.map(station => (
              <SelectItem key={station.value} value={station.value}>{station.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {hasRole(['admin', 'rh']) && (
          <div className="flex items-center gap-1.5 shrink-0">
            <Button variant="outline" onClick={() => navigate('/performance/import')} data-testid="import-performance-button" className="h-9 w-9 p-0" title="Stats">
              <BarChart3 size={16} />
            </Button>
            
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="import-csv-button" className="h-9 w-9 p-0" title="Import CSV">
                  <Upload size={16} />
                </Button>
              </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Importer des salariés via CSV</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
                      <h4 className="font-medium text-blue-900 mb-2">Format CSV</h4>
                      <code className="block bg-white p-2 rounded text-xs">
                        Matricule;Nom;Prenom;Email;Telephone;Poste;Type_Contrat;Date_Embauche
                      </code>
                    </div>
                    <Button variant="outline" onClick={downloadTemplate} className="w-full">
                      <Download size={18} className="mr-2" />
                      Télécharger le modèle
                    </Button>
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                      <Upload className="mx-auto h-12 w-12 text-slate-400 mb-4" />
                      <label htmlFor="csv-upload" className="cursor-pointer">
                        <span className="text-sm text-slate-600">Sélectionner un fichier CSV</span>
                        <input id="csv-upload" type="file" accept=".csv" onChange={handleFileUpload} disabled={importing} className="hidden" />
                      </label>
                      {importing && <div className="mt-4 animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>}
                    </div>
                    {importResult && (
                      <div className={`p-4 rounded-lg ${importResult.erreurs === 0 ? 'bg-green-50' : 'bg-amber-50'}`}>
                        <p>Importés: {importResult.importes} | Erreurs: {importResult.erreurs}</p>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>

            <Button onClick={() => navigate('/employees/new')} data-testid="add-employee-button" variant="brand" className="h-9 w-9 p-0" title="Ajouter">
              <Plus size={16} />
            </Button>
          </div>
        )}
      </div>

      {/* Compteur */}
      <p className={cn("text-xs text-center", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>
        {filteredEmployees.length} salarié{filteredEmployees.length > 1 ? 's' : ''}
      </p>

      {/* Liste des employés - Centrée */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
        {filteredEmployees.map((employee) => (
          <EmployeeCard
            key={employee.id}
            employee={employee}
            highlightTerm={highlightTerm}
            onDelete={handleDeleteEmployee}
            onEdit={(id) => navigate(`/employees/${id}?edit=true`)}
            onView={(id) => navigate(`/employees/${id}`)}
            onGradeChange={handleGradeChange}
            canEditGrade={canEditGrade}
            hasAdminRole={hasRole(['admin', 'rh', 'responsable', 'manager'])}
            deletingId={deletingId}
            theme={theme}
            isExpanded={expandedId === employee.id}
            onToggle={() => handleToggleExpand(employee.id)}
          />
        ))}
      </div>

      {filteredEmployees.length === 0 && (
        <div className={cn(
          "text-center py-12 rounded-xl border max-w-5xl mx-auto",
          theme === 'dark' ? 'bg-zinc-900/50 border-white/10 text-zinc-500' : 'bg-white border-gray-200 text-gray-500'
        )}>
          Aucun salarié trouvé
        </div>
      )}
    </div>
  );
}
